import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {z} from 'zod';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {DateTime} from 'luxon';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {NO_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {UTCTimezone} from 'common/src/modules/timezones/timezones.consts';
import {
  ScheduleNodeOutputData,
  ScheduleType,
} from 'common/src/modules/decisionTree/decisionTree.types';
import {NodeType, OutputType} from 'common/src/modules/nodes/nodes.types';
import {scheduleOutputSchema} from '../../../../schemas/scheduleCardSchema';
import _findLastIndex from 'lodash/findLastIndex';
import {ScheduleCardScratchpadState} from '../../scratchpad/scheduleCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import validateScheduleOutput from '../../utils/schedule/validateScheduleOutput';

const addScheduleOutputInputSchema = z.object({
  scheduleOutput: scheduleOutputSchema,
});

export default function addScheduleOutputTool() {
  async function execute(
    params: ToolExecuteParams<typeof addScheduleOutputInputSchema, ScheduleCardScratchpadState>,
  ) {
    const {scheduleOutput} = params.input;
    const {agentScratchpad} = params.toolUtils;
    const validatedScratchpad = validateAgentScratchpad<ScheduleCardScratchpadState>(
      agentScratchpad,
      NodeType.SCHEDULE,
    );

    const error = validateScheduleOutput(
      scheduleOutput,
      validatedScratchpad.card.scheduleType as ScheduleType,
    );

    if (error) {
      return toolError(error);
    }

    const nextId =
      validatedScratchpad.card.outputs.length > 0
        ? Math.max(...validatedScratchpad.card.outputs.map((o) => o.id), 0) + 1
        : 1;

    const newOutput: ScheduleNodeOutputData = {
      id: nextId,
      nodeId: NO_NODE_ID,
      name: scheduleOutput.name ?? `Output ${nextId}`,
      type: OutputType.Regular,
      dateRange:
        scheduleOutput.dateRange && scheduleOutput.dateRange.start && scheduleOutput.dateRange.end
          ? {
              start: DateTime.fromISO(scheduleOutput.dateRange.start, {
                zone: UTCTimezone,
                setZone: true,
              }).toUTC(),
              end: DateTime.fromISO(scheduleOutput.dateRange.end, {
                zone: UTCTimezone,
                setZone: true,
              }).toUTC(),
            }
          : undefined,
      daysOfWeek:
        scheduleOutput.daysOfWeek && scheduleOutput.daysOfWeek.length > 0
          ? scheduleOutput.daysOfWeek.map((dow) => ({
              days: dow.days ?? [],
              start_minute: (dow.startHour ?? 0) * 60,
              end_minute: ((dow.endHour ?? 0) + 1) * 60,
            }))
          : undefined,
    };

    const lastRegularIndex = _findLastIndex(
      validatedScratchpad.card.outputs,
      (output) => output.type === OutputType.Regular,
    );

    if (lastRegularIndex === -1) {
      validatedScratchpad.card.outputs.unshift(newOutput);
    } else {
      validatedScratchpad.card.outputs.splice(lastRegularIndex + 1, 0, newOutput);
    }

    return toolResult('Schedule output added');
  }

  return tool<typeof addScheduleOutputInputSchema, ScheduleCardScratchpadState>({
    name: V2CardAgentToolName.AddScheduleOutput,
    displayName: 'Adding schedule output',
    description: 'Add a schedule output (validated against schedule type).',
    inputSchema: addScheduleOutputInputSchema,
    execute,
  });
}
